/**
 * Phase 1.1 — Canonical production-state accessor for tickets.
 *
 * Doctrine: Bucket 3 — Parking Lot, Physical Location, Rollover, and EOD
 *   §3.2 "Physical location does not determine production state."
 *   §3.4.1–§3.4.11  ticket states relevant to the parking lot
 *   §3.5.1          true-unassigned is limited to estimates + completed-awaiting-pickup
 *
 * Why this helper exists
 * ----------------------
 * Parking-related and lifecycle states (Scheduled-but-Parked, Held-in-Parking,
 * Parking-Lot-inactive, Completed Awaiting Pickup, Picked Up, etc.) are
 * currently inferred ad-hoc from combinations of `parking_lot`,
 * `parking_spot_id`, `bay_id`, `paused_at`, `assigned_tech_id`,
 * `scheduled_tech_id`, `completed_at`, `picked_up_at`, `deleted_at`,
 * `archived_at`, `timer_status`, `is_estimate`, and `status`. A future
 * refactor could accidentally merge two distinct states. `getProductionState`
 * resolves the production state from a single permissive shape, derived in
 * one place, so consumers (UI, board-issue detector, OptiAssign, audit) can
 * read one canonical value instead of re-deriving each time.
 *
 * Phase 1.1 scope: ACCESSOR + TESTS ONLY.
 *   - This helper is additive. It does NOT change scheduling, cascade,
 *     OptiAssign, Parking Lot, Due Out, ticket-action, or picked-up/comeback
 *     behavior.
 *   - It does NOT introduce a DB column, enum, or migration. The DB schema
 *     remains the source of truth; this helper just reads it consistently.
 *   - Callers are NOT broadly migrated yet. Adoption happens in later slices.
 *
 * Builds on:
 *   - `isTicketDeleted` (DMC-D2) — soft-delete predicate.
 *   - `isInParkingLot` / `isPhysicallyLocated` (DMC-E) — parking triad.
 *
 * Precedence (terminal/inactive resolved before active):
 *   1.  deleted
 *   2.  archived
 *   3.  picked_up
 *   4.  completed_awaiting_pickup
 *   5.  held_in_parking
 *   6.  held_in_bay
 *   7.  active_in_bay
 *   8.  scheduled_but_parked
 *   9.  scheduled
 *  10.  parking_lot_inactive
 *  11.  estimate_prospect    (also the safe fallback)
 *
 * Cancelled tickets: terminal, in the `deleted` bucket. Every cancellation
 * since 8G.2B (`terminalize_noncompletion_locked`) writes a `deleted_at`
 * tombstone, so it already resolves at step 1 and shows where deleted work
 * shows (OptiBoard Deleted / DeletedTickets, "cancelled — cannot be
 * restored"). A legacy `status = 'Cancelled'` row WITHOUT the tombstone used to
 * fall through to `scheduled` (active production, consuming capacity); it now
 * lands in the same terminal bucket as every other cancellation
 * (fix/foh-optiboard-calendar-truth). No separate `cancelled` state: nothing
 * reads one, and cancelled work is not production either way.
 */

import { isTicketDeleted, type DeletedStateTicketShape } from './ticketDeletedState';

/**
 * The canonical production-state union for a ticket.
 *
 * String literals (not an enum) keep this helper tree-shakeable, easy to
 * serialise into audit rows, and obvious in DevTools — matching the style
 * of `BoardIssueType` and the `status` union on `Ticket`.
 */
export type ProductionState =
  | 'deleted'
  | 'archived'
  | 'picked_up'
  | 'completed_awaiting_pickup'
  | 'held_in_parking'
  | 'held_in_bay'
  | 'active_in_bay'
  | 'scheduled_but_parked'
  | 'scheduled'
  | 'parking_lot_inactive'
  | 'estimate_prospect';

/**
 * Frozen list of all canonical states in precedence order. Useful for
 * exhaustiveness checks and for tests that want to enumerate states.
 */
export const PRODUCTION_STATES = Object.freeze([
  'deleted',
  'archived',
  'picked_up',
  'completed_awaiting_pickup',
  'held_in_parking',
  'held_in_bay',
  'active_in_bay',
  'scheduled_but_parked',
  'scheduled',
  'parking_lot_inactive',
  'estimate_prospect',
] as const) satisfies ReadonlyArray<ProductionState>;

/**
 * Permissive input shape for `getProductionState`.
 *
 * Mirrors the philosophy of `DeletedStateTicketShape` and
 * `ParkingLotStateTicketShape`: every field is optional so callers can pass
 * partial DB rows, query projections, snapshot rows, or the legacy camelCase
 * `Ticket` type (most of these snake_case fields are read via `(t as any)`
 * at the call site today). The helper is forgiving on missing fields and
 * falls back to `estimate_prospect` rather than throwing.
 */
export interface ProductionStateTicketShape extends DeletedStateTicketShape {
  // Soft-delete inherited from DeletedStateTicketShape: status, deleted_at
  archived_at?: string | Date | null;
  picked_up_at?: string | Date | null;
  completed_at?: string | Date | null;
  // Hold / pause representations
  paused_at?: string | Date | null;
  pause_reason?: string | null;
  // Physical location triad (mirrors ParkingLotStateTicketShape)
  parking_lot?: boolean | null;
  parking_spot_id?: string | null;
  bay_id?: string | null;
  // Timer state and devirginization marker
  timer_status?: string | null;
  actually_started?: boolean | null;
  // Production / dispatch plan
  is_estimate?: boolean | null;
  assigned_tech_id?: string | null;
  scheduled_tech_id?: string | null;
  scheduled_date?: string | null;
  // string from the DB row; Date after in-memory parsing. Subtype shapes
  // (Waiter/PullForward/Absence/…) carry `string | Date | null`, so the base
  // must admit both or they cannot legally extend it (TS2430/TS2320).
  scheduled_start_time?: string | Date | null;
}

/**
 * Internal predicate: does the ticket have a HOLD signal that should be
 * treated as "paused / held"?
 *
 * Two reps because the codebase carries both:
 *   - `status === 'Paused'` (canonical 6-status enum)
 *   - `paused_at != null` (timer-state column)
 * Either is sufficient. Mirrors the defense-in-depth posture used in
 * `isTicketDeleted` for `status === 'Deleted'` vs `deleted_at`.
 */
function isHeld(ticket: ProductionStateTicketShape): boolean {
  return ticket.status === 'Paused' || ticket.paused_at != null;
}

/**
 * Internal predicate: is this ticket actively occupying a bay/lane?
 *
 * Two signals:
 *   - `timer_status === 'running'` (timer is live in a bay), OR
 *   - `actually_started === true` AND `bay_id != null` (devirginized + in bay)
 * "Held in bay" is handled separately at a higher precedence — a held
 * ticket that is bay-blocking resolves there, not here.
 */
function isActiveInBay(ticket: ProductionStateTicketShape): boolean {
  if (ticket.timer_status === 'running') return true;
  if (ticket.actually_started === true && ticket.bay_id != null) return true;
  return false;
}

/**
 * Internal predicate: is the ticket physically in the parking lot?
 *
 * Mirrors `isInParkingLot`/`isPhysicallyLocated` from `parkingLotState.ts`:
 * either the isolation flag (`parking_lot === true`) or a specific spot
 * (`parking_spot_id != null`) qualifies as "parked".
 */
function isParked(ticket: ProductionStateTicketShape): boolean {
  return ticket.parking_lot === true || ticket.parking_spot_id != null;
}

/**
 * Internal predicate: does the ticket carry an active production-plan
 * signal that survives a parking transition?
 *
 * Per the Bucket 2 §2.12.4 / Bucket 3 §3.4.3 doctrine, a Scheduled-but-
 * Parked ticket retains its dispatch plan even when the parking immunity
 * rule nulls `assigned_tech_id` (see comment in TechnicianColumn ~L97).
 * `scheduled_tech_id` is the survival-of-park signal added by migration
 * 20260601160000.
 */
function hasActiveDispatchPlan(ticket: ProductionStateTicketShape): boolean {
  return (
    ticket.assigned_tech_id != null ||
    ticket.scheduled_tech_id != null ||
    ticket.scheduled_date != null ||
    ticket.scheduled_start_time != null
  );
}

/**
 * Canonical production-state accessor.
 *
 * Returns the single canonical `ProductionState` for the ticket, resolving
 * terminal/inactive states before active ones. See the precedence list at
 * the top of this file.
 *
 * The accessor is pure: it depends only on the input shape, never reads
 * the clock, never queries the DB, never mutates anything.
 */
export function getProductionState(ticket: ProductionStateTicketShape): ProductionState {
  // 1. Terminal: soft-deleted (deleted_at != null OR status === 'Deleted').
  //    Wins over any other field combination per DMC-D2 doctrine.
  if (isTicketDeleted(ticket)) return 'deleted';
  //    Cancelled is terminal too — see the header (legacy rows without the
  //    deleted_at tombstone every modern cancellation carries).
  if (ticket.status === 'Cancelled') return 'deleted';

  // 2. Terminal: archived (DB/history record, no normal operational presence).
  if (ticket.archived_at != null) return 'archived';

  // 3. The vehicle has physically left. `picked_up_at` alone is sufficient,
  //    and 8G.2C.7 makes that permissiveness LOAD-BEARING rather than merely
  //    tolerant. The comment here used to assert a data-model invariant that a
  //    pickup could not happen without a completion first. That invariant no
  //    longer holds: Picked Up Incomplete leaves `status='Blocked'` with
  //    `completed_at` NULL, so a departure is not a completion — which is
  //    exactly why this test comes BEFORE the `completed_at` test below.
  if (ticket.picked_up_at != null) return 'picked_up';

  // 4. Production done, vehicle still at shop: Completed Awaiting Pickup.
  if (ticket.completed_at != null) return 'completed_awaiting_pickup';

  // 5–6. Held (paused) variants. Held splits by physical location:
  //   - Held in a parking spot (or general lot) → 'held_in_parking'
  //   - Held while still occupying a bay         → 'held_in_bay'
  if (isHeld(ticket)) {
    if (ticket.bay_id != null) return 'held_in_bay';
    if (isParked(ticket)) return 'held_in_parking';
    // Held with neither bay nor parking spot — uncommon. Treat as held-in-
    // parking (the "off-bay" hold variant) rather than active-in-bay.
    return 'held_in_parking';
  }

  // 7. Active in bay (running timer or devirginized work in a bay).
  if (isActiveInBay(ticket)) return 'active_in_bay';

  // 7b. An estimate is not production, wherever the car sits. Not started
  //     and not held, it is a prospect — even when it was given a parking
  //     spot at creation (CreateTicket's estimate path sets parking_lot and the
  //     start_time trigger fills scheduled_date, which made a parked estimate
  //     look like `scheduled_but_parked`: active Scheduled work consuming
  //     capacity). Step 9 already required `is_estimate !== true`; step 8 did
  //     not.
  if (ticket.is_estimate === true) return 'estimate_prospect';

  // 8. Scheduled-but-Parked: physically parked but with an active dispatch
  //    plan (Bucket 3 §3.4.3 — remains active production work).
  if (isParked(ticket) && hasActiveDispatchPlan(ticket)) {
    return 'scheduled_but_parked';
  }

  // 9. Scheduled: has dispatch plan, not in any above state, not an
  //    estimate. (`is_estimate === false` may also be undefined for legacy
  //    rows; default to treating it as a scheduled ticket when a dispatch
  //    plan signal is present.)
  //    (Estimates already left at 7b.)
  if (hasActiveDispatchPlan(ticket)) {
    return 'scheduled';
  }

  // 10. Parking-Lot-inactive: explicitly parked with no production plan.
  //     The cascade filter in `globalCascade.ts` already excludes
  //     `parking_lot=true` from active scheduling; this state names that
  //     condition canonically.
  if (isParked(ticket)) return 'parking_lot_inactive';

  // 11. Fallback: Estimate / Prospect. Catches `is_estimate === true` and
  //     also rows that carry no production signal at all.
  return 'estimate_prospect';
}

// =============================================================================
// Phase 1.3 — Held-in-Parking explicit predicates
// =============================================================================
//
// Doctrine: Bucket 3 §3.4.6 Held-in-Parking, §3.10.2 Held-in-Parking AutoBump
// and cascade interaction.
//
// Held-in-Parking is the explicit state for a paused/held ticket whose
// vehicle is physically out of the bay (parked or in a parking spot) but
// whose production work is incomplete. Per doctrine it:
//
//   - is not bay-blocking (does not consume current bay occupancy)
//   - is production-relevant (visible, schedulable, may rejoin the bay)
//   - is Due Out relevant while incomplete (Bucket 4.5 §4.5.9)
//   - is distinct from `parking_lot_inactive` (no active production plan)
//   - is distinct from `completed_awaiting_pickup` (production is done)
//   - is distinct from `held_in_bay` (vehicle still occupying the bay)
//
// The predicates below give callers a stable, doctrine-aligned vocabulary
// for these properties without forcing them to enumerate state values
// inline. Phase 1.3 scope is predicates + tests only — no behavior is
// rewired. Callers will adopt these in later slices.

/**
 * Convenience predicate: is this ticket currently in the canonical
 * `held_in_parking` production state?
 *
 * Equivalent to `getProductionState(ticket) === 'held_in_parking'` — provided
 * as a named predicate so call sites can read intent at a glance and match
 * the existing `isTicketDeleted` / `isInParkingLot` convention.
 */
export function isHeldInParking(ticket: ProductionStateTicketShape): boolean {
  return getProductionState(ticket) === 'held_in_parking';
}

/**
 * Does this production state currently consume bay/lane capacity?
 *
 * Bay-blocking states (per Bucket 3 §3.4.4 / §3.4.5):
 *   - `active_in_bay` — running timer or devirginized work in a bay
 *   - `held_in_bay`   — paused while the vehicle still occupies the bay
 *
 * All other states (including `held_in_parking`, `scheduled_but_parked`,
 * `parking_lot_inactive`, `completed_awaiting_pickup`, `picked_up`) do NOT
 * consume current bay occupancy.
 *
 * Use this predicate when computing current bay capacity, lane cascade
 * inclusion, or any "is the bay free right now" decision.
 */
export function isBayBlockingProductionState(state: ProductionState): boolean {
  return state === 'active_in_bay' || state === 'held_in_bay';
}

/**
 * Is this state an active production state — i.e., still part of the
 * Dayboard / OptiBoard active production set and capable of consuming
 * future schedule capacity?
 *
 * Production-relevant states (per Bucket 3 §3.4):
 *   - `scheduled_but_parked` (Bucket 3 §3.4.3)
 *   - `scheduled`            (Bucket 3 §3.4.2)
 *   - `active_in_bay`        (Bucket 3 §3.4.4)
 *   - `held_in_bay`          (Bucket 3 §3.4.5)
 *   - `held_in_parking`      (Bucket 3 §3.4.6)
 *
 * Non-production-relevant: `deleted`, `archived`, `picked_up`,
 * `completed_awaiting_pickup` (terminal lifecycle), `parking_lot_inactive`
 * (explicitly removed from active scheduling), `estimate_prospect`
 * (Bucket 3 §3.4.1 — "should not be treated as active Dayboard/OptiBoard
 * production work until scheduled").
 */
export function isProductionRelevantState(state: ProductionState): boolean {
  switch (state) {
    case 'scheduled_but_parked':
    case 'scheduled':
    case 'active_in_bay':
    case 'held_in_bay':
    case 'held_in_parking':
      return true;
    case 'deleted':
    case 'archived':
    case 'picked_up':
    case 'completed_awaiting_pickup':
    case 'parking_lot_inactive':
    case 'estimate_prospect':
      return false;
  }
}

/**
 * Should Due Out risk be tracked for this state right now?
 *
 * Per Bucket 4.5 §4.5.6, Due Out applies while production work is
 * incomplete. Once required production work is completed on or before
 * Due Out (i.e., the ticket reaches `completed_awaiting_pickup` or
 * `picked_up`), Due Out as an active production risk is satisfied
 * (Bucket 4.5 §4.5.2 — "Pickup delay after on-time completion is not a
 * production Due Out breach").
 *
 * In practice this set is the same as the production-relevant set. It is
 * provided as a separate predicate so the intent at adoption sites
 * (board-issue detector, Due Out resolver, audit) is explicit, and so a
 * future divergence (e.g., adding `cancelled` to one set but not the other)
 * can be made without re-auditing every caller.
 */
export function isDueOutRelevantState(state: ProductionState): boolean {
  return isProductionRelevantState(state);
}

// =============================================================================
// Phase 1.4 — Picked Up immutability / correction-workflow vocabulary
// =============================================================================
//
// Doctrine: Bucket 3 §3.4.9 Picked Up Ticket, §3.8.4 Completed Awaiting
// Pickup reopen rules, §3.8.5 Picked Up Tickets and Reopen Behavior.
//
// Picked Up is a customer-lifecycle terminal state. Per doctrine, once a
// vehicle is picked up:
//   - Required production work is complete.
//   - The ticket should not return to active production through normal
//     drag/drop, OptiAssign, cascade, parking moves, timer starts, job
//     edits, or other active-production mutations.
//   - If pickup was entered in error, undoing it requires an authorized
//     correction workflow.
//   - If the customer truly returns, a new linked comeback / warranty /
//     correction ticket should be created instead of silently reactivating
//     the original.
//
// Current codebase state (verified read-only in this slice):
//   - DB CHECK constraint L10 (migration csi_i3_a2_lifecycle_active_
//     paused_pickup_check_constraint, 2026-05-15) blocks physical-location
//     moves on picked-up rows: picked_up_at IS NOT NULL ⇒ no bay_id, no
//     parking_spot_id, NOT parking_lot=true.
//   - The TicketKebabMenu hides active-production actions under
//     `isCompleted = status === 'Completed'` (line 1003), which catches
//     both completed_awaiting_pickup AND picked_up.
//   - The only path that clears `picked_up_at` is the kebab "Reactivate"
//     action (handleReactivate, line 517), gated on `canRestoreTickets`
//     (OWNER / MANAGER / ORG_ADMIN). It clears the timestamp wholesale
//     without preserving the original pickup as audit-column history.
//     This is the doctrinal gap that a future "Picked Up Correction"
//     workflow should address (Bucket 3 §3.8.5).
//
// Phase 1.4 scope: predicates + tests only. No mutation behavior, kebab,
// RPC, or workflow is changed. Predicates give callers stable, doctrine-
// aligned vocabulary that future slices will adopt when they harden the
// correction workflow and tighten guards.

/**
 * Is this state the canonical `picked_up` terminal state?
 *
 * Pure state-level predicate (no ticket fields read). Provided alongside
 * `isPickedUpTicket` for callers that already have a `ProductionState`
 * value and want to ask the question without re-deriving.
 */
export function isPickedUpState(state: ProductionState): boolean {
  return state === 'picked_up';
}

/**
 * Convenience: is this ticket currently in the `picked_up` state?
 *
 * Equivalent to `isPickedUpState(getProductionState(ticket))`. Named
 * predicate so call-sites read naturally — sister to `isHeldInParking`
 * (Phase 1.3) and the long-established `isTicketDeleted` (DMC-D2).
 */
export function isPickedUpTicket(ticket: ProductionStateTicketShape): boolean {
  return isPickedUpState(getProductionState(ticket));
}

/**
 * Can this ticket currently accept "normal active-production mutations"?
 *
 * Normal active-production mutations include: drag/drop on OptiBoard, kebab
 * Change Date, reassign, manual schedule move, start timer, pause/resume,
 * add/edit/delete jobs, move to parking, move to bay, etc.
 *
 * Returns TRUE for the five production-relevant states (Bucket 3 §3.4.2–
 * §3.4.6):
 *   - scheduled
 *   - scheduled_but_parked
 *   - active_in_bay
 *   - held_in_bay
 *   - held_in_parking
 *
 * Returns FALSE for:
 *   - deleted, archived   — must restore first (terminal soft/hard)
 *   - picked_up           — requires picked-up correction workflow (§3.8.5)
 *   - completed_awaiting_pickup — requires explicit Reactivate (§3.8.4)
 *   - parking_lot_inactive — requires reintroduction through approved workflow
 *   - estimate_prospect    — requires conversion via OptiAssign / OptiPlace
 *
 * Intent at call sites: this is the gate for "would a normal click-to-act
 * UI affordance work right now?" — if FALSE, the caller should hide /
 * disable the affordance or route the user through the correct authorized
 * workflow.
 *
 * NOTE: This predicate is the gate POLICY. Phase 1.4 deliberately does NOT
 * wire it into any caller — adoption happens in later slices. The existing
 * kebab gate `status === 'Completed'` overlaps this gate today, which is
 * why no behavior change is needed yet.
 */
export function canPerformActiveProductionMutation(
  ticket: ProductionStateTicketShape,
): boolean {
  return isProductionRelevantState(getProductionState(ticket));
}

/**
 * Should this ticket be routed through the picked-up correction workflow
 * rather than handled by a normal action?
 *
 * Returns TRUE only for `picked_up`. Distinct from
 * `!canPerformActiveProductionMutation` because the OTHER blocked states
 * (deleted, archived, completed_awaiting_pickup, parking_lot_inactive,
 * estimate_prospect) each have their own established correction paths
 * (restore-from-deleted, unarchive, Reactivate-from-completed, reintroduce-
 * from-parking-lot, convert-estimate). Only `picked_up` currently lacks a
 * dedicated correction workflow — per Bucket 3 §3.8.5, this should land
 * as its own slice that preserves the original pickup history.
 *
 * When the dedicated workflow is added, callers can ask this predicate
 * instead of branching on the state literal. The shape of the workflow
 * itself is intentionally NOT defined here — Phase 1.4 only provides the
 * routing question.
 */
export function requiresPickedUpCorrectionWorkflow(
  ticket: ProductionStateTicketShape,
): boolean {
  return isPickedUpTicket(ticket);
}

// =============================================================================
// Phase 3.1 — Scheduled-but-Parked active scheduling vocabulary
// =============================================================================
//
// Doctrine: Bucket 3 §3.4.3 Scheduled-but-Parked Ticket.
//
// Scheduled-but-Parked is a *physically parked but production-active*
// ticket. Per doctrine it:
//
//   - is visible on the Dayboard / OptiBoard (lifecycle Scheduled tab
//     unless work has begun).
//   - is production-relevant (covered by Phase 1.3 `isProductionRelevantState`).
//   - is Due Out relevant while incomplete (Phase 1.3 `isDueOutRelevantState`).
//   - is NOT bay-blocking (Phase 1.3 `isBayBlockingProductionState` returns
//     false for it — vehicle is currently in a parking spot or general
//     lot, not in a bay).
//   - requires bay confirmation before timer start (Phase 2.4
//     `requiresBayConfirmationBeforeTimerStart` returns true).
//   - is distinct from `parking_lot_inactive` (no production plan),
//     `held_in_parking` (paused), `completed_awaiting_pickup` (production
//     done), and `picked_up` (vehicle gone).
//
// The two helpers below give callers a named predicate so adoption sites
// (capacity planners, OptiBoard tab routing, board-issue detector) can
// read intent at a glance, mirroring `isHeldInParking` (Phase 1.3) and
// `isPickedUpTicket` (Phase 1.4).
//
// Phase 3.1 scope is predicates + tests only. No tab query, scheduling
// writer, AutoBump, cascade, or Due Out detector behavior is changed.

/**
 * Convenience: is this ticket currently in the `scheduled_but_parked`
 * production state?
 *
 * Equivalent to `getProductionState(ticket) === 'scheduled_but_parked'`.
 * Named predicate so call sites read naturally — sister to
 * `isHeldInParking` (Phase 1.3) and `isPickedUpTicket` (Phase 1.4).
 */
export function isScheduledButParked(ticket: ProductionStateTicketShape): boolean {
  return getProductionState(ticket) === 'scheduled_but_parked';
}

/**
 * Is this state an "active scheduled production" state — i.e., the
 * ticket has a dispatch plan, is in production-active scheduling, but
 * is NOT currently active in a bay?
 *
 * Returns TRUE for:
 *   - `scheduled`            — has tech/date, no work started, not parked
 *   - `scheduled_but_parked` — has tech/date, vehicle in parking spot/lot,
 *                              awaiting pull-in
 *
 * Returns FALSE for:
 *   - `active_in_bay`, `held_in_bay` — already in bay (use
 *                                     `isBayBlockingProductionState` for
 *                                     those)
 *   - `held_in_parking`             — paused production, NOT a scheduled
 *                                     planning state (it's mid-execution
 *                                     with the vehicle moved out of bay)
 *   - terminal/inactive states      — not in active scheduling at all
 *
 * Intent: callers asking "is this ticket still in the planning phase,
 * not yet pulled into a bay?" — useful for OptiAssign candidate scoring,
 * lane capacity planning, and Scheduled-tab routing.
 */
export function isActiveScheduledProductionState(state: ProductionState): boolean {
  return state === 'scheduled' || state === 'scheduled_but_parked';
}

// =============================================================================
// Phase 3.3 — Active-work rollover eligibility
// =============================================================================
//
// Doctrine:
//   - Bucket 3 §3.4.7  Rollover / Continuation Ticket
//   - Bucket 3 §3.4.8  Completed Awaiting Pickup — not rollover material
//   - Bucket 3 §3.4.9  Picked Up Ticket — not active production work
//   - Bucket 3 §3.4.11 Archived — DB/history only
//   - Bucket 3 §3.11   Rollover Rules — General
//   - Bucket 3 §3.11.5 Held-in-Parking Rollover
//   - Bucket 4 §4.6.3  Active Rollover / Continuation Guard
//
// Why these predicates exist
// --------------------------
// 8G.2C.7 CORRECTION. This paragraph used to say "Picked Up always implies
// `completed_at`" and "Picked Up carries `status='Completed'`". BOTH ARE NOW
// FALSE. Picked Up Incomplete is a supported act since 8G.2C, and it leaves:
//
//     status = 'Blocked', picked_up_at set, completed_at NULL
//
// so a departure no longer implies a completion. `picked_up_at` is a PHYSICAL
// fact — a vehicle left the property — and says nothing about whether the work
// was finished. The precedence list above already encodes that: `picked_up`
// outranks `completed_awaiting_pickup`.
//
// It also cited `eodRolloverHandler.ts`, which no longer exists in this
// repository. The live rollover engine is `parking-lot-rollover/index.ts`, and
// it refuses tickets outside `status IN ('Active','Paused')` — which DOES
// exclude an incomplete pickup, because that lands in 'Blocked'. The exclusion
// is still correct, but for a reason unrelated to `completed_at`.
//
// The predicates below are unaffected: they key on the production state, and
// `getProductionState` returns `picked_up` from `picked_up_at` alone, so both
// kinds of departure are excluded from active-work rollover.
//
// These predicates pin the doctrine so future rollover refactors cannot
// accidentally let Picked Up (or Completed Awaiting Pickup, Deleted,
// Archived, Parking-Lot-inactive, Estimate/Prospect) roll as active work.
// Phase 3.3 scope: PREDICATES + TESTS ONLY. No rollover-engine writer is
// modified. The paired test pins both the predicates and the current
// rollover-engine exclusion behavior via source-scan.
//
// Eligibility set (production-relevant states per Bucket 3 §3.4):
//   - `scheduled`             — unresolved scheduled work
//   - `scheduled_but_parked`  — unresolved parked scheduled work
//   - `active_in_bay`         — running work that may need EOD continuation
//   - `held_in_bay`           — paused-in-bay work
//   - `held_in_parking`       — Bucket 3 §3.11.5 says these "may roll
//                               forward as an active production concern
//                               without consuming current bay capacity."
//
// Excluded set:
//   - `picked_up`               — the vehicle has physically left, whether the
//                                  visit finished or not; customer returns go
//                                  through a NEW linked ticket
//                                  (Bucket 3 §3.8.5)
//   - `completed_awaiting_pickup` — production done; Due Out satisfied
//   - `deleted` / `archived`    — terminal; not active work
//   - `parking_lot_inactive`    — no active dispatch plan
//   - `estimate_prospect`       — needs conversion first

/**
 * Reasons a ticket may be excluded from active-work rollover. Used by
 * the diagnostic helper below for audit/log clarity.
 *
 *   - 'picked_up'                 — customer pickup complete
 *   - 'completed_awaiting_pickup' — production done, vehicle parked
 *   - 'deleted'                   — soft-deleted
 *   - 'archived'                  — archived (DB/history only)
 *   - 'parking_lot_inactive'      — parked with no active plan
 *   - 'estimate_prospect'         — needs conversion to scheduled work
 *   - null                        — eligible to roll as active work
 */
export type ActiveWorkRolloverExclusionReason =
  | 'picked_up'
  | 'completed_awaiting_pickup'
  | 'deleted'
  | 'archived'
  | 'parking_lot_inactive'
  | 'estimate_prospect';

/**
 * Pure state predicate: is this production state eligible to roll
 * forward as active production work?
 *
 * Equivalent to `isProductionRelevantState(state)`. Provided as a
 * separately-named predicate so the intent at adoption sites (rollover
 * engine guards, EOD continuation, parking-lot rollover) is explicit,
 * and so a future divergence (e.g., excluding `held_in_parking` from a
 * specific rollover path) can be made without re-auditing every caller.
 */
export function isRolloverEligibleProductionState(state: ProductionState): boolean {
  return isProductionRelevantState(state);
}

/**
 * Convenience: can this ticket roll forward as active production work?
 *
 * Equivalent to `isRolloverEligibleProductionState(getProductionState(ticket))`.
 * Named predicate so call sites read naturally — sister to
 * `canPerformActiveProductionMutation` (Phase 1.4) and
 * `canMoveTicketToParking` (Phase 2.5).
 */
export function canTicketRollAsActiveWork(ticket: ProductionStateTicketShape): boolean {
  return isRolloverEligibleProductionState(getProductionState(ticket));
}

/**
 * Diagnostic helper: why is this ticket excluded from active-work
 * rollover? Returns `null` when the ticket is eligible.
 *
 * The returned reason mirrors the canonical production state name for
 * audit/log clarity. Callers that want to render a user-facing message
 * can map this to copy.
 */
export function getRolloverExclusionReason(
  ticket: ProductionStateTicketShape,
): ActiveWorkRolloverExclusionReason | null {
  const state = getProductionState(ticket);
  switch (state) {
    case 'picked_up':
      return 'picked_up';
    case 'completed_awaiting_pickup':
      return 'completed_awaiting_pickup';
    case 'deleted':
      return 'deleted';
    case 'archived':
      return 'archived';
    case 'parking_lot_inactive':
      return 'parking_lot_inactive';
    case 'estimate_prospect':
      return 'estimate_prospect';
    case 'scheduled':
    case 'scheduled_but_parked':
    case 'active_in_bay':
    case 'held_in_bay':
    case 'held_in_parking':
      return null;
  }
}
